import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, take, catchError, switchMap } from 'rxjs/operators';
import { ApiService } from '../../../Core/Api/Service/api.service';
import { environment } from '../../../../environments/environment';
import { 
  AccessCodeGenerateRequest, 
  AccessCodeGenerateResponse, 
  AccessCodeBatchResponse,
  AccessCodeGenerationResult 
} from '../Models/access-code.model';

@Injectable({
  providedIn: 'root'
})
export class AccessCodesService {

  private cachedBatch: AccessCodeBatchResponse | null = null;
  private cachedBatchId: string | null = null;

  constructor(
    private apiService: ApiService,
  ) { }

  /**
   * Step 1: Generate a new batch of access codes
   * Returns a batch ID that can be used to retrieve the codes
   */
  public generateAccessCodeBatch(data: AccessCodeGenerateRequest): Observable<AccessCodeGenerateResponse> {
    const body: any = {
      security: "MZ07V8RzcUT412O0l0TZJ2vg5ny28OEK",
      codeType: data.codeType,
      numKeys: data.numKeys,
      maxNumberOfUses: data.maxNumberOfUses,
      adminName: data.adminName,
      comment: data.comment
    };

    if (data.codeType === 2) {
      body.numberOfDays = data.numberOfDays;
    } else {
      body.expirationDate = data.expirationDate;
    }

    return this.apiService.post(`${environment.trogonApi}/v1/admin/accesscode/batch`, body, {}).pipe(
      take(1),
      map((res: AccessCodeGenerateResponse) => res),
      catchError((error) => {
        console.error('Error generating access code batch:', error);
        throw error;
      })
    );
  }

  /**
   * Step 2: Retrieve the batch details and generated codes
   * Checks cache first - if cached batchId matches requested batchId, returns cached data
   * Otherwise fetches from API and caches the result
   */
  public getAccessCodeBatch(batchId: string): Observable<AccessCodeBatchResponse> {
    if (this.cachedBatch && this.cachedBatchId === batchId) {
      return of(this.cachedBatch);
    }

    return this.apiService.get(`${environment.trogonApi}/v1/admin/accesscode/batch?id=${batchId}`, {}).pipe(
      take(1),
      map((res: AccessCodeBatchResponse) => {
        // Cache the result
        this.cachedBatch = res;
        this.cachedBatchId = batchId;
        return res;
      }),
      catchError((error) => {
        console.error('Error retrieving access code batch:', error);
        throw error;
      })
    );
  }

  /**
   * Complete flow: Generate batch and retrieve codes in one operation
   * This is the method most components will want to use
   * Caches the batch data after retrieval
   */
  public generateAndRetrieveAccessCodes(data: AccessCodeGenerateRequest): Observable<AccessCodeGenerationResult> {
    return this.generateAccessCodeBatch(data).pipe(
      switchMap((generateResponse: AccessCodeGenerateResponse) => {
        return this.getAccessCodeBatch(generateResponse.id).pipe(
          map((batchResponse: AccessCodeBatchResponse) => {
            return {
              batchId: generateResponse.id,
              batch: batchResponse
            };
          })
        );
      }),
      catchError((error) => {
        console.error('Error in complete access code generation flow:', error);
        throw error;
      })
    );
  }

  /**
   * Export access codes batch as CSV file
   * @param batchId The ID of the batch to export
   * @returns Observable<Blob> representing the downloadable CSV file
   */
  public exportAccessCodeBatch(batchId: string): Observable<Blob> {
    return this.getAccessCodeBatch(batchId).pipe(
      map((batchData: AccessCodeBatchResponse) => {
        const csvContent = this.generateCsvFromBatch(batchData, batchId);
        return new Blob([csvContent], { type: 'text/csv' });
      }),
      catchError((error) => {
        console.error('Error exporting access code batch:', error);
        throw error;
      })
    );
  }

  private generateCsvFromBatch(batchData: AccessCodeBatchResponse, batchId: string): string {
    const expirationText = batchData.codeType === 2 ? 'Number of Days' : 'Expiration Date';
    
    let csvContent = `Code,${expirationText},Remaining Uses,Max Uses\n`;
    
    batchData.codes.forEach(code => {
      const expirationValue = batchData.codeType === 2 
        ? (code.numberOfDays?.toString() || '')
        : (code.expirationDate || '');
      
      csvContent += `="${code.code}",${expirationValue},${code.usagesRemaining},${batchData.maxNumberOfUses}\n`;
    });
    
    csvContent += `Batch ID,${batchId}\n`;
    
    return csvContent;
  }

  /**
   * Get the CSV filename with current date format
   */
  public getCsvFilename(): string {
    const currentDate = new Date();
    const dateFormat = `${(currentDate.getMonth() + 1).toString().padStart(2, '0')}${currentDate.getDate().toString().padStart(2, '0')}${currentDate.getFullYear().toString().slice(-2)}`;
    return `REEFAccessCodes_${dateFormat}.csv`;
  }

  /**
   * Clear the cached batch data
   * Called on logout to ensure no data persists between sessions
   */
  public clearCache(): void {
    this.cachedBatch = null;
    this.cachedBatchId = null;
  }

}
